import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  WARRANTY_STATUS_LABELS,
  WARRANTY_STATUS_ORDER,
  warrantyStatusSchema,
  type Warranty,
  type WarrantyStatus,
} from "@shared/modules/x-garanties";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Input, Select } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { WarrantyStatusBadge } from "../components/status-badge";
import { listWarranties } from "../lib/api";
import { formatDayShort } from "../lib/dates";

/** Liste des garanties : numéro, client, véhicule, dates, statut, recherche et filtre. */
export function GarantiesListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const parsedStatus = warrantyStatusSchema.safeParse(searchParams.get("statut"));
  const status: WarrantyStatus | "" = parsedStatus.success ? parsedStatus.data : "";
  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const [debounced, setDebounced] = useState(query.trim());
  const [items, setItems] = useState<Warranty[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listWarranties({ status: status ? [status] : undefined, q: debounced || undefined, limit: 500 })
      .then((list) => {
        if (cancelled) return;
        setItems(list);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, debounced]);

  const selectStatus = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("statut", value);
    else next.delete("statut");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="page garanties">
      <PageHeader
        title="Garanties"
        subtitle="Certificats de garantie antirouille 10 ans : brouillons, émis, signés."
        actions={
          <Button
            variant="primary"
            icon={<Icon name="plus" size={16} />}
            onClick={() => navigate("/garanties/nouvelle")}
          >
            Nouvelle garantie
          </Button>
        }
      />

      <div className="garanties-toolbar">
        <div className="garanties-toolbar__search">
          <Input
            type="search"
            aria-label="Rechercher"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Numéro, client, NIV, véhicule…"
          />
        </div>
        <Select
          aria-label="Statut"
          className="garanties-toolbar__select"
          value={status}
          onChange={(e) => selectStatus(e.target.value)}
        >
          <option value="">Tous les statuts</option>
          {WARRANTY_STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {WARRANTY_STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
        <div className="garanties-toolbar__spacer" />
        <span className="garanties-toolbar__count">
          {loading ? "…" : `${items.length} garantie${items.length > 1 ? "s" : ""}`}
        </span>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Numéro</th>
            <th>Client</th>
            <th>Véhicule</th>
            <th>Émise le</th>
            <th>Expire le</th>
            <th>Statut</th>
          </tr>
        </thead>
        <tbody>
          {items.map((w) => (
            <tr
              key={w.id}
              className="table__row--link"
              onClick={() => navigate(`/garanties/${w.id}`)}
            >
              <td>
                <Link className="garanties__number" to={`/garanties/${w.id}`}>
                  {w.number}
                </Link>
              </td>
              <td>
                <span className="garanties__client">{w.clientName}</span>
                {w.clientPhone ? <span className="garanties__sub">{w.clientPhone}</span> : null}
              </td>
              <td>
                <span>{w.vehicleLabel}</span>
                {w.vin ? (
                  <span className="garanties__sub garanties__vin">{w.vin}</span>
                ) : (
                  <span className="garanties__sub garanties__missing">NIV manquant</span>
                )}
              </td>
              <td className="garanties__date">{formatDayShort(w.issuedAt)}</td>
              <td className="garanties__date">{formatDayShort(w.expiresAt)}</td>
              <td>
                <WarrantyStatusBadge status={w.status} />
              </td>
            </tr>
          ))}
          {!loading && items.length === 0 ? (
            <tr>
              <td colSpan={6} className="table__empty">
                {failed
                  ? "Liste indisponible — réessayez dans un instant."
                  : debounced || status
                    ? "Aucune garantie ne correspond."
                    : "Aucune garantie. Émettez la première depuis un travail terminé ou avec « Nouvelle garantie »."}
              </td>
            </tr>
          ) : null}
          {loading && items.length === 0 ? (
            <tr>
              <td colSpan={6} className="table__empty">
                Chargement…
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
